import { ref, Ref, shallowRef, watch } from "vue";
import { IFieldRefs } from "@/core/libs/FormRequester/types/IFieldRefs.ts";
import { IFieldDefinition } from "@/core/libs/FormRequester/types/IFieldDefinition.ts";
import { ICheckAllowedFn } from "@/core/libs/FormRequester/types/ICheckAllowedFn.ts";
import defaultCheckAllowed from "@/core/libs/FormRequester/defaultCheckAllowed.ts";
import { IBasicObject } from "@/interfaces/IBasicObject.ts";
import BaseService, { RequestError, RequestState } from "@/services/BaseService.ts";
import { FormError } from "@/repositories/dto/errors/FormError.ts";


export default class FormRequester<T, K> {
  private _fieldsRefs: IBasicObject<IFieldRefs> = {};
  private _isLoading: Ref<boolean> = ref(false);
  private _formError: Ref<string | null> = ref(null);
  private _isAllowContinue: Ref<boolean> = ref(false);
  private _formRequestStateRef: Ref<RequestState>;
  private _formRequestValueRef: Ref<K | null>;
  private _formRequestErrorRef: Ref<RequestError | null>;
  private _serviceRefKey: string;
  private _service: T;
  private _method: string;

  public constructor(formKey: string, service: T, method: string, private fieldDefinitions: IBasicObject<IFieldDefinition>, defaultValue: K, private isAllowedFn: ICheckAllowedFn = defaultCheckAllowed) {
    this._serviceRefKey = "FORM_REF_" + formKey;
    this._service = service;
    this._method = method;

    const { state, value, error } = (service as BaseService).getRefs<K>(this._serviceRefKey, defaultValue);

    this._formRequestStateRef = state;
    this._formRequestValueRef = value;
    this._formRequestErrorRef = error;

    watch(this._formRequestStateRef, (value: RequestState) => {
      switch (value) {
        case RequestState.Loading:
          this._isLoading.value = true;
          break;
        case RequestState.Ready:
        default:
          this._isLoading.value = false;
      }
    });

    watch(this._formRequestErrorRef, (value: RequestError | FormError | null) => {
      if (value instanceof FormError) {
        for (const _key in value.fields) {
          const _keyRef = this._fieldsRefs[_key];
          if (!_keyRef) {
            continue;
          }
          _keyRef.errors.value = value.fields[_key];
          _keyRef.isError.value = true;
        }
      } else {
        this._formError.value = value?.message ?? "unknown";
      }
    });

    for (const [key, def] of Object.entries(fieldDefinitions)) {
      switch (def.type as unknown as string) {
        case "number":
          this.addField<number>(key);
          break;
        case "bool":
        case "boolean":
          this.addField<boolean>(key);
          break;
        case "string":
        default:
          this.addField<string>(key);
      }
    }

    this.checkIsAllowed();

    this.addWatchFields();
  }

  public get formRefs(): { state: Ref<RequestState>; value: Ref<K | null>; error: Ref<RequestError | null>; } {
    return {
      state: this._formRequestStateRef,
      value: this._formRequestValueRef,
      error: this._formRequestErrorRef
    };
  }

  public get isAllowContinue(): Ref<boolean> {
    return shallowRef(this._isAllowContinue);
  }

  public get formError(): Ref<string | null> {
    return this._formError;
  }

  public get fieldsRefs(): IBasicObject<IFieldRefs> {
    return this._fieldsRefs;
  }

  public get isLoading(): Ref<boolean> {
    return this._isLoading;
  }

  private checkIsAllowed(): boolean {
    const fields: IBasicObject<any> = {};
    for (const [key, fieldRef] of Object.entries(this.fieldsRefs)) {
      fields[key] = fieldRef.model.value;
    }
    return this._isAllowContinue.value = this.isAllowedFn(fields, this.fieldDefinitions);
  }

  private addWatchFields(): void {
    watch(Object.values(this._fieldsRefs).map((refs: IFieldRefs) => {
      return refs.model;
    }), () => {
      this._formError.value = "";
      this._isAllowContinue.value = this.runValidators();
      if (this._isAllowContinue.value) {
        this.checkIsAllowed();
      }
    });
  }

  public addField<E>(key: string): IFieldRefs {
    return this._fieldsRefs[key] = {
      model: shallowRef<E>(),
      errors: shallowRef([]),
      isDisabled: shallowRef(false),
      isError: shallowRef(false)
    };
  }

  public clearErrors(): void {
    this._formError.value = null;
    for (const [, fieldRef] of Object.entries(this.fieldsRefs)) {
      fieldRef.isError.value = false;
      fieldRef.errors.value = [];
    }
  }

  public async send(): Promise<void> {
    this.clearErrors();
    if (this._service[this._method]) {
      const payload = {};
      for (const [key, { model }] of Object.entries(this._fieldsRefs)) {
        payload[key] = model.value;
      }
      await this._service[this._method](this._serviceRefKey, payload);
    }
  }

  private getFieldValue(key: string): any {
    return this.fieldsRefs[key].model.value;
  }

  private runValidators(): boolean {
    let allValid = true;
    for (const [key, definition] of Object.entries(this.fieldDefinitions)) {
      if (!definition.validator) {
        continue;
      }
      const error = definition.validator(this.getFieldValue(key));
      const isValid = !error;
      this.fieldsRefs[key].errors.value = isValid ? [] : [error];
      this.fieldsRefs[key].isError.value = !isValid;
      allValid = allValid && isValid;
    }

    return allValid;
  }
}
